import type { Fish, Subscription } from '@Types/fish';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardDescription,
  CardHeader,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { formatPhone } from '../../utils';
import { VariantProps } from 'class-variance-authority';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import ShowFor from '@Components/ShowFor';

/**
 * A Card displaying details about a Fish
 *
 * @param index - pass to have as the key when mapping
 * @param setFishCardIndex - setter for an index state variable to know which mapped card was clicked
 * @param fish - when mapped from the fish details membership panel: this represents the Fish that owns the sharedSubscription
 * or if it's a single FishCard: just pass the Fish you want displayed on the card
 * @param subscription - represents the subscription object when mapped from the fish details membership panel
 * @param displayName - used when mapped (IE the fish details membership panel): if omitted falls back to whatever Fish is passed
 * @param deleteDialogOpen - represents state var to handle an associated Dialog being open
 * @param setDeleteDialogOpen - corresponding setter to control deleteDialogOpen state
 * @param setDeleteNameForSharedSub - setter to display the name in above delete Dialog when clicked
 * @param showDeleteButton - If present/true we display the trash icon
 */

export default function FishCard({
  index,
  setFishCardIndex,
  fish,
  subscription,
  displayName,
  deleteDialogOpen,
  setDeleteDialogOpen,
  setDeleteNameForSharedSub,
  showDeleteButton,
}: {
  index?: number;
  setFishCardIndex?: Function;
  fish: Fish;
  subscription?: Subscription & { fish: Fish };
  displayName?: string;
  deleteDialogOpen: boolean;
  setDeleteDialogOpen: Function;
  setDeleteNameForSharedSub?: Function;
  buttonText?: string;
  buttonVariant?: VariantProps<typeof Button>['variant'];
  showDeleteButton?: boolean;
}) {
  return (
    <div>
      <Card
        key={`fish-card-subscription-index-${index || 0}`}
        className="bg-white py-4"
      >
        <CardHeader className="text-2xl text-zinc-100">
          <div className="flex flex-row justify-between">
            <div className="flex w-1/2 flex-row gap-4">
              {subscription?.fish?.mostRecentAsset?.cloudinaryId ? (
                <div className="h-12 w-12 overflow-hidden rounded-full bg-black">
                  <CloudinaryImage
                    publicId={subscription?.fish?.mostRecentAsset?.cloudinaryId}
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <div className="h-12 w-12 overflow-hidden rounded-full bg-black">
                  <img src="/profile-placeholder.jpg" alt="Landmark Logo" />
                </div>
              )}
              <div className="flex flex-col truncate">
                <CardDescription className="truncate text-xl text-black">
                  {displayName
                    ? displayName
                    : fish?.memberProfile
                      ? fish?.memberProfile?.fName +
                        ' ' +
                        fish?.memberProfile?.lName
                      : fish?.fishProfiles?.[0]?.fName +
                        ' ' +
                        fish?.fishProfiles?.[0]?.lName}
                  <div className="text-lg text-zinc-600">
                    {subscription
                      ? formatPhone(subscription?.fish?.phoneNumber)
                      : formatPhone(fish?.phoneNumber) || ''}
                  </div>
                </CardDescription>
              </div>
            </div>

            {showDeleteButton &&
              deleteDialogOpen !== undefined &&
              typeof setDeleteDialogOpen === 'function' && (
                <ShowFor permissions={['MEMBERS_WRITE']}>
                  <div
                    className="flex cursor-pointer flex-row"
                    onClick={() => {
                      setDeleteDialogOpen(true);

                      if (setDeleteNameForSharedSub) {
                        setDeleteNameForSharedSub(displayName);
                      }
                      if (setFishCardIndex) {
                        setFishCardIndex(index);
                      }
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faTrashCan}
                      className="text-black"
                      size={'xs'}
                    />
                  </div>
                </ShowFor>
              )}
          </div>
        </CardHeader>
      </Card>
    </div>
  );
}
